import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Check, XCircle, AlertCircle, Upload, Edit2, Sparkles, Tag, Plus } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { getCustomerTags, createCustomerTag, CustomerTag } from '../lib/customers';

interface ParsedCustomer {
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
  notes: string | null;
  status: 'active' | 'inactive';
}

interface CustomerImportPreviewModalProps {
  isOpen: boolean;
  onClose: () => void;
  customers: ParsedCustomer[];
  onImport: (customers: ParsedCustomer[], tagIds: string[]) => Promise<void>;
  businessId: string;
}

const TAG_COLORS = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#6366F1', '#14B8A6',
];

export default function CustomerImportPreviewModal({
  isOpen,
  onClose,
  customers,
  onImport,
  businessId
}: CustomerImportPreviewModalProps) {
  const [editableCustomers, setEditableCustomers] = useState<ParsedCustomer[]>(customers);
  const [isImporting, setIsImporting] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<ParsedCustomer | null>(null);
  const [refinementPrompt, setRefinementPrompt] = useState('');
  const [isRefining, setIsRefining] = useState(false);
  const [showRefinementInput, setShowRefinementInput] = useState(false);

  const [availableTags, setAvailableTags] = useState<CustomerTag[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([]);
  const [newTagName, setNewTagName] = useState('');
  const [isCreatingTag, setIsCreatingTag] = useState(false);
  const [showTagInput, setShowTagInput] = useState(false);

  useEffect(() => {
    if (isOpen && businessId) {
      getCustomerTags(businessId).then(setAvailableTags).catch(() => {});
    }
  }, [isOpen, businessId]);

  // Update editable customers when customers prop changes
  React.useEffect(() => {
    setEditableCustomers(customers);
  }, [customers]);

  const toggleTag = (tagId: string) => {
    setSelectedTagIds(prev =>
      prev.includes(tagId) ? prev.filter(id => id !== tagId) : [...prev, tagId]
    );
  };

  const handleCreateTag = async () => {
    const name = newTagName.trim();
    if (!name || !businessId) return;
    setIsCreatingTag(true);
    try {
      const color = TAG_COLORS[availableTags.length % TAG_COLORS.length];
      const tag = await createCustomerTag(businessId, name, color);
      setAvailableTags(prev => [...prev, tag]);
      setSelectedTagIds(prev => [...prev, tag.id]);
      setNewTagName('');
      setShowTagInput(false);
    } catch (err: any) {
      toast.error(err.message || 'Failed to create tag');
    } finally {
      setIsCreatingTag(false);
    }
  };

  if (!isOpen) return null;

  const handleEdit = (index: number) => {
    setEditingIndex(index);
    setEditForm({ ...editableCustomers[index] });
  };

  const handleSaveEdit = () => {
    if (editingIndex !== null && editForm) {
      const updated = [...editableCustomers];
      updated[editingIndex] = editForm;
      setEditableCustomers(updated);
      setEditingIndex(null);
      setEditForm(null);
    }
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setEditForm(null);
  };

  const handleRemove = (index: number) => {
    const updated = editableCustomers.filter((_, i) => i !== index);
    setEditableCustomers(updated);
  };

  const handleRefine = async () => {
    if (!refinementPrompt.trim()) {
      toast.error('Please enter a refinement instruction');
      return;
    }

    setIsRefining(true);
    try {
      const { data, error } = await supabase.functions.invoke('parse-csv-customers', {
        body: {
          businessId,
          action: 'refine',
          customers: editableCustomers,
          refinementPrompt: refinementPrompt.trim()
        }
      });

      if (error) {
        throw error;
      }

      if (!data?.success || !data?.customers) {
        throw new Error(data?.error || 'Failed to refine customer list');
      }

      setEditableCustomers(data.customers);
      setRefinementPrompt('');
      setShowRefinementInput(false);
      
      if (data.removed > 0) {
        toast.success(`Refined list: ${data.removed} customer${data.removed !== 1 ? 's' : ''} removed. ${data.customers.length} remaining.`);
      } else {
        toast.success('List refined successfully');
      }
    } catch (error) {
      console.error('Error refining customers:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to refine customer list');
    } finally {
      setIsRefining(false);
    }
  };

  const handleImport = async () => {
    if (editableCustomers.length === 0) {
      return;
    }

    setIsImporting(true);
    try {
      await onImport(editableCustomers, selectedTagIds);
      onClose();
    } catch (error) {
      console.error('Import error:', error);
    } finally {
      setIsImporting(false);
    }
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 640;

  const modalContent = (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[10005] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div 
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col ${
          isMobile ? 'h-full max-h-full rounded-none' : ''
        }`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Review Import</h2>
            <p className="text-sm text-gray-500 mt-1">
              {editableCustomers.length} customer{editableCustomers.length !== 1 ? 's' : ''} ready to import
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRefinementInput(!showRefinementInput)}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors flex items-center gap-2"
              disabled={isRefining || editableCustomers.length === 0}
            >
              <Sparkles className="h-4 w-4" />
              {showRefinementInput ? 'Cancel Refine' : 'Refine with AI'}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Refinement Input */}
        {showRefinementInput && (
          <div className="p-6 border-b border-gray-200 bg-purple-50">
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Refinement Instruction
                </label>
                <textarea
                  value={refinementPrompt}
                  onChange={(e) => setRefinementPrompt(e.target.value)}
                  placeholder="e.g., Remove any customers with email containing 'ccr', or Keep only customers with phone numbers, or Remove customers with status 'inactive'"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none"
                  rows={3}
                  disabled={isRefining}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Tell the AI how to refine your customer list. Examples: "Remove customers with email containing 'ccr'", "Keep only active customers", "Remove duplicates"
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleRefine}
                  disabled={!refinementPrompt.trim() || isRefining || editableCustomers.length === 0}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isRefining ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Refining...
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-4 w-4" />
                      Apply Refinement
                    </>
                  )}
                </button>
                <button
                  onClick={() => {
                    setShowRefinementInput(false);
                    setRefinementPrompt('');
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  disabled={isRefining}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Tags Section */}
        <div className="px-6 pt-4 pb-2">
          <div className="bg-gray-50 rounded-xl border border-gray-200 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Tag className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-semibold text-gray-700">Apply Tags to Import</span>
              </div>
              {!showTagInput && (
                <button
                  onClick={() => setShowTagInput(true)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                >
                  <Plus className="h-3 w-3" />
                  New Tag
                </button>
              )}
            </div>

            {availableTags.length > 0 && (
              <div className="flex flex-wrap gap-2 mb-2">
                {availableTags.map(tag => {
                  const isSelected = selectedTagIds.includes(tag.id);
                  return (
                    <button
                      key={tag.id}
                      onClick={() => toggleTag(tag.id)}
                      className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
                        isSelected
                          ? 'ring-2 ring-offset-1 ring-blue-400 shadow-sm'
                          : 'opacity-60 hover:opacity-100'
                      }`}
                      style={{
                        backgroundColor: `${tag.color || '#3B82F6'}20`,
                        color: tag.color || '#3B82F6',
                        borderWidth: 1,
                        borderColor: `${tag.color || '#3B82F6'}40`,
                      }}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                      {tag.name}
                    </button>
                  );
                })}
              </div>
            )}

            {availableTags.length === 0 && !showTagInput && (
              <p className="text-xs text-gray-400 mb-1">No tags yet. Create one to apply to all imported customers.</p>
            )}

            {showTagInput && (
              <div className="flex items-center gap-2 mt-2">
                <input
                  type="text"
                  value={newTagName}
                  onChange={(e) => setNewTagName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleCreateTag(); }}
                  placeholder="Tag name (e.g. Realtors)"
                  className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  autoFocus
                  disabled={isCreatingTag}
                />
                <button
                  onClick={handleCreateTag}
                  disabled={!newTagName.trim() || isCreatingTag}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
                >
                  {isCreatingTag ? '...' : 'Add'}
                </button>
                <button
                  onClick={() => { setShowTagInput(false); setNewTagName(''); }}
                  className="px-3 py-1.5 bg-gray-200 text-gray-600 rounded-lg text-sm font-medium hover:bg-gray-300 transition-colors"
                  disabled={isCreatingTag}
                >
                  Cancel
                </button>
              </div>
            )}

            {selectedTagIds.length > 0 && (
              <p className="text-xs text-gray-500 mt-2">
                {selectedTagIds.length} tag{selectedTagIds.length !== 1 ? 's' : ''} will be applied to all {editableCustomers.length} imported customer{editableCustomers.length !== 1 ? 's' : ''}
              </p>
            )}
          </div>
        </div>

        {/* Customer List */}
        <div className="flex-1 overflow-y-auto p-6">
          {editableCustomers.length === 0 ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-500">No customers to import</p>
            </div>
          ) : (
            <div className="space-y-3">
              {editableCustomers.map((customer, index) => (
                <div
                  key={index}
                  className="border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
                >
                  {editingIndex === index ? (
                    <div className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">First Name</label>
                          <input
                            type="text"
                            value={editForm?.first_name || ''}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, first_name: e.target.value } : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Last Name</label>
                          <input
                            type="text"
                            value={editForm?.last_name || ''}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, last_name: e.target.value } : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Email</label>
                          <input
                            type="email"
                            value={editForm?.email || ''}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, email: e.target.value || null } : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-700 mb-1">Phone</label>
                          <input
                            type="tel"
                            value={editForm?.phone || ''}
                            onChange={(e) => setEditForm(prev => prev ? { ...prev, phone: e.target.value || null } : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={handleSaveEdit}
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancelEdit}
                          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-300"
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">
                            {customer.first_name} {customer.last_name}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            customer.status === 'active' 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {customer.status}
                          </span>
                        </div>
                        <div className="space-y-1 text-sm text-gray-600">
                          {customer.email && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Email:</span>
                              <span>{customer.email}</span>
                            </div>
                          )}
                          {customer.phone && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Phone:</span>
                              <span>{customer.phone}</span>
                            </div>
                          )}
                          {customer.address && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Address:</span>
                              <span>{customer.address}</span>
                            </div>
                          )}
                          {customer.notes && (
                            <div className="flex items-center gap-2">
                              <span className="font-medium">Notes:</span>
                              <span>{customer.notes}</span>
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="flex gap-2 ml-4">
                        <button
                          onClick={() => handleEdit(index)}
                          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <Edit2 className="h-4 w-4 text-gray-600" />
                        </button>
                        <button
                          onClick={() => handleRemove(index)}
                          className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                          title="Remove"
                        >
                          <XCircle className="h-4 w-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-6 flex items-center justify-between">
          <div className="text-sm text-gray-600">
            {editableCustomers.length} customer{editableCustomers.length !== 1 ? 's' : ''} ready to import
          </div>
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300 transition-colors"
              disabled={isImporting}
            >
              Cancel
            </button>
            <button
              onClick={handleImport}
              disabled={isImporting || editableCustomers.length === 0}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Import {editableCustomers.length} Customer{editableCustomers.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
}

